import React, { useState, useRef, useEffect } from 'react';
import styles from './JoinBridgeButton.module.css';
import { useAuthFetch } from '../../../utils/authFetch';
import { trackJoinBridgeClick } from '../../../utils/modernUITracking';

function JoinBridgeButton({ problemId, newrelic }) {
  const fetchWithAuth = useAuthFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchingRef = useRef(false);

  // Clear stale error when the user navigates to a different incident
  useEffect(() => { setError(null); }, [problemId]);

  const handleJoin = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/v1/problem/${problemId}/bridge`);

      if (!response.ok) {
        const outcome = response.status === 404 ? 'not_found' : 'error';
        const message = response.status === 404
          ? 'No conference bridge is configured for this incident.'
          : 'Unable to retrieve the conference bridge. Please try again.';

        trackJoinBridgeClick(problemId, outcome, response.status, newrelic);
        setError(message);
        return;
      }

      const data = await response.json();

      try {
        const parsed = new URL(data.url);
        if (!['https:', 'http:'].includes(parsed.protocol)) {
          trackJoinBridgeClick(problemId, 'error', 200, newrelic);
          setError('Conference bridge URL has an invalid format.');
          return;
        }
      } catch {
        trackJoinBridgeClick(problemId, 'error', 200, newrelic);
        setError('Conference bridge URL has an invalid format.');
        return;
      }

      trackJoinBridgeClick(problemId, 'success', 200, newrelic);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      trackJoinBridgeClick(problemId, 'error', null, newrelic);
      setError('Unable to retrieve the conference bridge. Please try again.');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  return (
    <div className={styles.bridgeSection}>
      <div className={styles.bridgeRow}>
        <span className={styles.bridgeLabel}>Bridge</span>
        <button
          className={styles.joinLink}
          onClick={handleJoin}
          disabled={loading}
          aria-label="Join the incident conference bridge meeting"
          aria-busy={loading}
        >
          {loading ? 'Joining\u2026' : 'Join Meeting'}
        </button>
      </div>

      {error && (
        <p className={styles.errorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default JoinBridgeButton;
